/**
 * Types for the non-solar Quote page line-item editors (GS-7411).
 *
 * Wire format mirrors what Studio writes on a system node so the same payload
 * can flow through proposal / cashflow / finance with no adapter layer:
 *  - adders are stored in `AdderData` shape (a row in `StudioSystemType.line_items`)
 *  - incentives are stored in `IncentiveStudioItem` shape
 *
 * The Quote-page UI only edits a small subset of `AdderData` (label, quantity,
 * value, tax_included). The other fields are filled in with the same defaults
 * Studio uses for a brand-new ad-hoc adder (see
 * `_normalise_adder` in `django/osapi/project/quote_system.py`). Numeric cells
 * use `null` for "empty" so the inline inputs render blank instead of forcing
 * a leading `0` that the browser would otherwise glue to whatever the pro types.
 */

export const FIXED_INCENTIVE = 3 as const

export type QuoteAdderRow = {
  /** Library id when sourced from `/adders/`; null/undefined for ad-hoc rows typed inline. */
  adder_id?: number | null
  label: string
  /** Edited inline. */
  quantity: number | null
  /** Unit price (tax-inclusive — same convention the proposal renders as "$X.XX Including $Y tax"). Edited inline. */
  value: number | null
  /** Tax rate percent (e.g. 10 means 10%). Used only to split the breakdown on the proposal — NOT a
   *  multiplier on `value`. `null` means no tax line. Edited inline. */
  tax_included: number | null
  /** Studio fields below are not edited by the Quote UI; defaults match the Adder Django model. */
  category?: number | null
  type?: number
  cost_value?: number
  /** Studio string form (`"fixed"`, …) or legacy int from older rows. */
  formula?: number | string
  show_customer?: boolean
  display_as_line_item?: boolean
  value_includes_tax?: boolean
  unit_label?: string | null
  dealer_fee_interaction_type?: number
  price_treatment?: number
  show_customer_cost?: boolean
}

export type QuoteIncentiveRow = {
  /** Library id when sourced from `/incentives/`; null/undefined for ad-hoc rows. */
  incentive_id?: number | null
  /** Fixed for ad-hoc rows; library picks may be Fixed or Price-Based. */
  incentive_type: number
  title: string
  /** Mirror of `settings.value`; the Quote page does no tax math, so we keep them in sync. */
  inc_tax: number | null
  settings: {
    value: number | null
    /** Defaults to `'price_including_tax'` per Studio's IncentiveStudioItem default. */
    price_based_incentive_type:
      | 'price_including_tax'
      | 'price_excluding_tax'
      | 'price_payable_including_tax'
      | 'price_payable_excluding_tax'
  }
}

/** Default values for the Studio-shape fields the Quote-page UI does not edit. Mirrors
 *  `_ADDER_DEFAULTS` in `quote_system.py` so client and server agree on what an ad-hoc
 *  Quote-page adder looks like at rest. */
export const QUOTE_ADDER_STUDIO_DEFAULTS = {
  category: null,
  type: 0,
  cost_value: 0,
  formula: 'fixed',
  show_customer: true,
  value_includes_tax: true,
  unit_label: null,
  dealer_fee_interaction_type: 0,
  price_treatment: 0,
  show_customer_cost: false,
  display_as_line_item: true,
} as const

/** Build an empty Studio-shape `IncentiveStudioItem` for the Quote page. Always fixed,
 *  installer-claimed (the library picker is filtered to that subset). */
export const buildEmptyQuoteIncentive = (): QuoteIncentiveRow => ({
  incentive_id: null,
  incentive_type: FIXED_INCENTIVE,
  title: '',
  inc_tax: null,
  settings: { value: null, price_based_incentive_type: 'price_including_tax' },
})
